"use client";
import React, { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  Typography,
  useMediaQuery,
  Stack,
  Fade,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorIcon from "@mui/icons-material/Error";
import { signOut } from "next-auth/react";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { Email as IconMail } from "@mui/icons-material";
import Link from "next/link";
import { cleanDisplayName, cleanName } from "@/lib/utils/nameUtils";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import { ProfileOrb } from "@/app/components/notifier/EnrichmentLoadingScreen";

const Profile = () => {
  const router = useRouter();
  const theme = useTheme();
  const profile = useSelector((state) => state.user.profile);
  const profileEndRef = useRef(null);
  const menuPaperRef = useRef(null);

  const lgDown = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [anchorEl, setAnchorEl] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [navigationState, setNavigationState] = useState({
    isNavigating: false,
    activeItem: null,
  });

  const minSwipeDistance = 50;

  const profilePicture =
    profile?.picture_custom || profile?.picture_cached || null;

  // Only flag as "not verified" when Hunter has explicitly returned a failure.
  // The default state (no Hunter run yet) trusts the LinkedIn-verified email.
  const isEmailVerified = profile?.email_verification_status !== "failed";

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const email = profile?.email || "";
  const displayEmail = email.length > 12 ? `${email.slice(0, 12)}...` : email;
  const handleMenuClose = () => {
    if (!navigationState.isNavigating) {
      setAnchorEl(null);
    }
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const onTouchMove = (e) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;

    const isRightSwipe =
      distanceX < -minSwipeDistance &&
      Math.abs(distanceY) < Math.abs(distanceX);
    const isLeftSwipe =
      distanceX > minSwipeDistance && Math.abs(distanceY) < Math.abs(distanceX);
    const isDownSwipe =
      distanceY < -minSwipeDistance &&
      Math.abs(distanceX) < Math.abs(distanceY);

    if (isRightSwipe || isDownSwipe) {
      handleMenuClose();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  const isInVideoMeeting = () => {
    return window.location.pathname.includes("/video");
  };

  const handleNavigation = (path, itemTitle) => {
    setNavigationState({
      isNavigating: true,
      activeItem: itemTitle,
    });

    if (isInVideoMeeting()) {
      const link = document.createElement("a");
      link.href = path;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setAnchorEl(null);
      setNavigationState({ isNavigating: false, activeItem: null });
    } else {
      router.push(path);

      setTimeout(() => {
        setAnchorEl(null);
        setNavigationState({ isNavigating: false, activeItem: null });
      }, 800);
    }
  };

  const handleOpenAvailability = () => {
    handleNavigation("/availability", "Availability");
  };

  const profileOptions = [
    {
      href: "/profile",
      onClick: () => handleNavigation("/profile", "My Profile"),
      title: "My Profile",
      subtitle: "Edit Bio, Rates & Visibility",
      icon: <PersonIcon />,
    },
    {
      href: "/availability",
      onClick: handleOpenAvailability,
      title: "Availability",
      subtitle: "Set Your Meeting Hours",
      icon: <EventIcon />,
    },
    {
      href: "/settings",
      onClick: () => handleNavigation("/settings", "Account Settings"),
      title: "Account Settings",
      subtitle: "Manage Notifications & Payments",
      icon: <SettingsIcon />,
    },
  ];

  const handleLogoutClick = () => {
    handleMenuClose();
    const signoutUrl =
      "/api/auth/signout?callbackUrl=" + encodeURIComponent("/login");

    if (window.location.pathname.includes("/video")) {
      const event = new CustomEvent("meetingNavigationRequest", {
        detail: { href: signoutUrl },
        cancelable: true,
        bubbles: false,
      });
      const shouldProceed = document.dispatchEvent(event);
      if (shouldProceed) {
        // Not in a joined meeting — show normal sign-out confirmation
        setIsSignOutDialogOpen(true);
      }
      // If cancelled, MeetingControls shows "Leave meeting?" first;
      // on confirm it will navigate to the signout URL.
    } else {
      setIsSignOutDialogOpen(true);
    }
  };

  const handleConfirmSignOut = async () => {
    setIsSigningOut(true);

    if (isInVideoMeeting()) {
      const link = document.createElement("a");
      link.href =
        "/api/auth/signout?callbackUrl=" + encodeURIComponent("/login");
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    try {
      await signOut({ callbackUrl: "/login", redirect: true });
    } catch (error) {
      console.error("Logout error:", error);
      setIsSigningOut(false);
      setIsSignOutDialogOpen(false);
    }
  };

  const handleCancelSignOut = () => {
    setIsSignOutDialogOpen(false);
  };

  const displayGivenName = cleanName(profile?.given_name || "");
  const displayFamilyName = cleanName(profile?.family_name || "");
  const displayFallbackName = cleanDisplayName(profile?.name || "");
  const displayBaseName =
    [displayGivenName, displayFamilyName].filter(Boolean).join(" ") ||
    displayFallbackName;
  const displayFullName = `${
    profile?.title ? profile.title + " " : ""
  }${displayBaseName}`.trim();

  return (
    <Box>
      <IconButton
        aria-label="Open profile menu"
        color="inherit"
        aria-controls="profile-menu"
        aria-haspopup="true"
        sx={typeof anchorEl === "object" ? { color: "primary.main" } : {}}
        onClick={handleMenuOpen}
      >
        <Avatar
          src={profilePicture}
          alt={
            profile
              ? `${profile.given_name} ${profile.family_name}'s profile picture`
              : "User avatar"
          }
          sx={{
            width: 35,
            height: 35,
          }}
        >
          {!profilePicture && <ProfileOrb theme={theme} />}
        </Avatar>
      </IconButton>
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.15)",
            backdropFilter: "blur(8px)",
          },
          "& .MuiMenu-paper": {
            width: lgDown ? "100%" : "430px",
            mt: lgDown ? 1 : 1.5,
            pb: 3,
            mr: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: lgDown ? "hidden" : "auto",
            "&::-webkit-scrollbar": {
              display: lgDown ? "none" : "block",
            },
            ...(isMobile && { touchAction: "pan-y" }),
          },
        }}
        slotProps={{
          transition: {
            timeout: 200,
          },
          list: {
            onTouchStart: isMobile ? onTouchStart : undefined,
            onTouchMove: isMobile ? onTouchMove : undefined,
            onTouchEnd: isMobile ? onTouchEnd : undefined,
            style: { width: "100%", height: "100%" },
          },
          paper: {
            ref: menuPaperRef,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            p: 4,
            flexGrow: 1,
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleMenuClose}
            disabled={navigationState.isNavigating}
            sx={{
              position: "absolute",
              top: 0,
              right: 8,
              opacity: navigationState.isNavigating ? 0.3 : 1,
              transition: "opacity 0.2s ease",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              py: 1,
              alignItems: "center",
            }}
          >
            <Avatar
              src={profilePicture}
              alt="ProfileImg"
              sx={{ width: 95, height: 95 }}
            >
              {!profilePicture && <ProfileOrb theme={theme} />}
            </Avatar>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  maxWidth: "240px",
                }}
              >
                <Typography
                  variant="body1"
                  color="textPrimary"
                  title={displayFullName}
                  sx={{
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {displayFullName}
                </Typography>
                <Tooltip title="View your public profile" arrow>
                  <IconButton
                    size="small"
                    component="a"
                    href={`/profile/${profile?.url || profile?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ p: 0.5, flexShrink: 0 }}
                    aria-label="View public profile"
                  >
                    <OpenInNewIcon
                      sx={{ fontSize: 14, color: "text.secondary" }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  width: "100%",
                  maxWidth: "240px",
                }}
              >
                <IconMail
                  sx={{
                    fontSize: 16,
                    color: theme.palette.text.secondary,
                    flexShrink: 0,
                  }}
                />
                <Tooltip title={email} arrow>
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.875rem",
                    }}
                  >
                    {displayEmail}
                  </Box>
                </Tooltip>

                {/* Email Verification Icon */}
                <Tooltip
                  title={isEmailVerified ? "Email verified" : "Not verified"}
                  arrow
                >
                  {isEmailVerified ? (
                    <VerifiedIcon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.text.secondary,
                      }}
                    />
                  ) : (
                    <ErrorIcon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.error.main,
                      }}
                    />
                  )}
                </Tooltip>

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!navigationState.isNavigating) {
                      handleNavigation("/settings", "Account Settings");
                    }
                  }}
                  disabled={navigationState.isNavigating}
                  sx={{
                    p: 0.5,
                    opacity: navigationState.isNavigating ? 0.5 : 1,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: navigationState.isNavigating
                        ? "transparent"
                        : "primary.light",
                    },
                  }}
                  aria-label="Edit profile settings"
                >
                  <EditIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </IconButton>
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            {profileOptions.map((option) => {
              const isActive = navigationState.activeItem === option.title;
              const isDisabled = navigationState.isNavigating && !isActive;

              return (
                <Link
                  key={option.title}
                  href={option.href}
                  style={{
                    textDecoration: "none",
                    pointerEvents: navigationState.isNavigating
                      ? "none"
                      : "auto",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!navigationState.isNavigating) {
                      option.onClick();
                    }
                  }}
                >
                  <Box
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      display: "block",
                      position: "relative",
                      opacity: isDisabled ? 0.3 : 1,
                      backgroundColor: isActive
                        ? "action.selected"
                        : "transparent",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor: navigationState.isNavigating
                          ? isActive
                            ? "action.selected"
                            : "transparent"
                          : "action.hover",
                        cursor: navigationState.isNavigating
                          ? "default"
                          : "pointer",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: "45px",
                          height: "45px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          position: "relative",
                        }}
                      >
                        {isActive ? (
                          <CircularProgress
                            size={20}
                            thickness={3}
                            sx={{
                              color: theme.palette.grey[400],
                              position: "absolute",
                            }}
                          />
                        ) : (
                          React.cloneElement(option.icon, {
                            sx: {
                              color: theme.palette.grey[350],
                              fontSize: 28,
                            },
                          })
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant={isMobile ? "subtitle1" : "body2"}
                          color={isActive ? "textPrimary" : "textPrimary"}
                          noWrap
                          sx={{
                            fontWeight: 600,
                            width: isMobile ? "100%" : "240px",
                            fontSize: isMobile ? "1rem" : undefined,
                            transition: "color 0.2s ease",
                          }}
                        >
                          {option.title}
                        </Typography>
                        <Typography
                          color="textSecondary"
                          variant={isMobile ? "body2" : "subtitle2"}
                          sx={{
                            width: isMobile ? "100%" : "240px",
                            opacity: isActive ? 0.7 : 1,
                            transition: "opacity 0.2s ease",
                            fontSize: isMobile ? "0.8rem" : "0.875rem",
                          }}
                          noWrap
                        >
                          {option.subtitle}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Link>
              );
            })}
          </Box>
        </Box>
        <Box
          ref={profileEndRef}
          sx={{
            my: 0,
            mx: isMobile ? 6 : 11,
            mb: 2.5,
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            fullWidth
            onClick={handleLogoutClick}
            disabled={navigationState.isNavigating}
            sx={{
              backgroundColor: theme.palette.grey[400],
              borderRadius: "25px",
              opacity: navigationState.isNavigating ? 0.5 : 1,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: navigationState.isNavigating
                  ? theme.palette.grey[400]
                  : theme.palette.grey[500],
              },
            }}
          >
            Sign out
          </Button>
        </Box>
      </Menu>
      <ConfirmDialog
        open={isSignOutDialogOpen}
        onClose={handleCancelSignOut}
        onConfirm={handleConfirmSignOut}
        title="Leaving your account"
        body="Are you sure you want to sign out of your account? You'll need to sign in again to access your dashboard."
        isProcessing={isSigningOut}
        processingText="Signing out"
        showIgnoreButton={false}
        confirmButtonText={isSigningOut ? "Signing out" : "Sign Out"}
        showCloseIcon={true}
        disableBackdropClick={isSigningOut}
        disableEscapeKeyDown={isSigningOut}
      />
    </Box>
  );
};

export default Profile;
